import { faCreditCard, faDollar, faGiftCard, faHouse, faMinus, faPlus, faWallet, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { GetCustomerCredit, GetCustomerHouseAccounts, GetCustomerPaymentMethods, ICustomerCreditCard } from "api/rpc/2024-04/customer/customer";
import { IGiftCard } from "api/rpc/2024-04/customer/payment/giftCard";
import { ICreditBook } from "api/rpc/2024-04/facilityAdmin/customer/customerCredit";
import axios, { CancelToken } from "axios";
import { ButtonNew as Button} from "components/buttonNew";
import { LocaleCurrency } from "helpers/Locale";
import { useAppDispatch } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { IFacility, TFacilityPaymentOption } from "redux/reducers/models/facility";
import "./customerPaymentMethods.scss";
import { ICustomerPaymentMethodState } from "pages/guest/Customer/ClientPortal/Booking/ClientPortalBooking";
import classNames from "classnames";
import ReactDOM from "react-dom";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import { valueToString } from "helpers/Helpers";
import Panel from "components/panel/Panel";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import Checkbox from "components/form/checkbox/Checkbox";
import CardSection from "components/cardSection/CardSection";
import { StripeCardCvcElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardNumberElementChangeEvent } from "@stripe/stripe-js";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { PostPaymentMethod, PostSetup } from "api/rpc/2024-04/customer/paymentMethod";
import { HouseAccountType } from "pages/secure/facility/customer/context/contextTypes";
import PaymentMethodIcon from "components/paymentMethodIcon/PaymentMethodIcon";
import Callout from "components/callout/Callout";
import Icon from "components/icon/FeatureIcon";


interface IGiftCardState {
    giftCards: Array<IGiftCard>;
    newGiftCard: boolean;
    validatedGiftCard: boolean;
}

interface ICreditBookState {
    creditBooks: Array<ICreditBook>;
}

interface IHouseAccountState {
    accounts: Array<HouseAccountType>;
}

interface ICardState {
    cards: Array<ICustomerCreditCard>;
}

interface INewCardState {
    active: boolean;
    save: boolean;
    elementComplete: {
        cardNumber: boolean;
        cardExpiry: boolean;
        cardCvc: boolean;
      };
}

interface IProps {
    customerId?: number;
    facility: Partial<IFacility>;
    paymentMethodsState: ICustomerPaymentMethodState;
    setPaymentMethodsState: React.Dispatch<React.SetStateAction<ICustomerPaymentMethodState>>;
    paymentOptions: Array<TFacilityPaymentOption>;
    validateGiftCard: () => Promise<boolean>
    remainingPaymentAmount?: number;
    creditCardRequired?: boolean;
    termsChecked?: boolean;
}

export default function CustomerPaymentMethods(props: IProps) {
    const { facility, customerId, paymentMethodsState, setPaymentMethodsState, paymentOptions , validateGiftCard, termsChecked, creditCardRequired, remainingPaymentAmount} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const elements = useElements();
    const stripe = useStripe();
    const windowSize = useWindowSize();
    const [paymentOptionState, setPaymentOptionState] = useState<Array<TFacilityPaymentOption & {open?: boolean}>>([]);

    const [newCardState, setNewCardState] = useState<INewCardState>({
        active: false,
        save: false,
        elementComplete: {
            cardNumber: false,
            cardExpiry: false,
            cardCvc: false,
        },
    });

    const [creditCardState, setCreditCardState] = useState<ICardState>({
        cards: [],
    });
    
    const [giftCardState, setGiftCardState] = useState<IGiftCardState>({
        giftCards: [],
        newGiftCard: false,
        validatedGiftCard: false,
    });
    const [creditBookState, setCreditBookState] = useState<ICreditBookState>({
        creditBooks: [],
    });

    const [houseAccountState, setHouseAccountState] = useState<IHouseAccountState>({
        accounts: [],
    });

    useEffect(() => {
        if (remainingPaymentAmount !== undefined) {
            if (remainingPaymentAmount <= 0 && !creditCardRequired) {
                setPaymentMethodsState(prev => ({...prev, creditCard: null}));
            }
        }
    }, [remainingPaymentAmount]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (facility) {
            void getCustomerPaymentMethods(source.token);
        }
        return () => source.cancel();
    }, [facility]);


    useEffect(() => {
        if (paymentOptions) {
            const facilityPaymentOptions = paymentOptions?.map(option => ({...option, open: option?.type === "card"}));
            const cardPaymentOption = paymentOptions?.find(option => option?.type === "card");
            ReactDOM.unstable_batchedUpdates(() => {
                setPaymentOptionState(facilityPaymentOptions);
                setPaymentMethodsState(prev => ({...prev, paymentOption: null, cardPaymentOption: cardPaymentOption}));
            });
        }
    }, [paymentOptions]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (customerId) {
            void getCreditBook(source.token);
            void getHouseAccounts(source.token);
        }
        return () => source.cancel();
    }, [customerId]);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (paymentMethodsState?.refreshPaymentMethods) {
            void refreshPaymentMethods(source.token);
        }
        return () => source.cancel();
    }, [paymentMethodsState?.refreshPaymentMethods]);

    async function refreshPaymentMethods(cancelToken: CancelToken) {
        const [cardRes, creditBookRes, accountRes] = await Promise.all([GetCustomerPaymentMethods({ client_id: facility?.client_id, type: ["gift_card"] },false,cancelToken), GetCustomerCredit({ customer_id: customerId }, false, cancelToken), GetCustomerHouseAccounts({ customer_id: customerId }, false, cancelToken)]);
        if (cardRes.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting customer payment methods")));
            return;
          }
          if (creditBookRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof creditBookRes?.data === "string" ? creditBookRes?.data : t("Error getting credit books")));
            return;
        }
        if (accountRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof accountRes?.data === "string" ? accountRes?.data : t("Error getting house accounts")));
            return;
        }

        const filteredGiftCards = cardRes?.data?.gift_card ? cardRes?.data?.gift_card?.filter((card: IGiftCard) => card?.balance > 0 && card?.status === "valid") : [];
        const filteredCreditBooks = creditBookRes?.data ? creditBookRes?.data?.filter((creditBook: ICreditBook) => creditBook?.balance > 0) : [];
        ReactDOM.unstable_batchedUpdates(() => {
            setGiftCardState(prev => ({...prev, giftCards: filteredGiftCards}));
            setCreditBookState(prev => ({...prev, creditBooks: filteredCreditBooks}));
            setHouseAccountState(prev => ({...prev, accounts: accountRes?.data}));
            setPaymentMethodsState(prev => ({...prev, paymentOption: null, giftCardCode: "", giftCardPin: "", creditBook: null, accountId: null, refreshPaymentMethods: false}));
        });
    }


    async function getCustomerPaymentMethods(cancelToken: CancelToken) {
        const customerPaymentMethodsRes = await GetCustomerPaymentMethods(
            { client_id: facility?.client_id, type: ["card", "gift_card"] },
            true,
            cancelToken,
          );

          if (customerPaymentMethodsRes.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting customer payment methods")));
            return;
          }
          const filteredGiftCards = customerPaymentMethodsRes?.data?.gift_card ? customerPaymentMethodsRes?.data?.gift_card?.filter((card: IGiftCard) => card?.balance > 0 && card?.status === "valid") : [];
          const autoSelectFirstCard = creditCardRequired !== undefined ? creditCardRequired : true;
          ReactDOM.unstable_batchedUpdates(() => {
            setGiftCardState(prev => ({...prev, giftCards: filteredGiftCards}));
            setCreditCardState(prev => ({...prev, cards: customerPaymentMethodsRes?.data?.card}));
            setPaymentMethodsState(prev => ({...prev, creditCard:  customerPaymentMethodsRes.data?.card?.length >= 1 && autoSelectFirstCard ? customerPaymentMethodsRes.data?.card[0] : undefined}));
          });
          
    }

    async function getCreditBook(cancelToken: CancelToken) {
        const res = await GetCustomerCredit({ customer_id: customerId }, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting credit books")));
            return;
        }
        const filteredCreditBooks = res?.data ? res?.data?.filter((creditBook: ICreditBook) => creditBook?.balance > 0) : [];
        setCreditBookState(prev => ({...prev, creditBooks: filteredCreditBooks}));
    }

    async function getHouseAccounts(cancelToken: CancelToken) {
        const res = await GetCustomerHouseAccounts({ customer_id: customerId }, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting house accounts")));
            return;
        }
        setHouseAccountState(prev => ({...prev, accounts: res?.data}));
    }

    function paymentOptionIcon(paymentOption: TFacilityPaymentOption) {
        switch(paymentOption.type) {
            case "card":
                return <div className="ui-customer-payment-methods-option-card"><FontAwesomeIcon icon={faCreditCard}/></div>;
            case "account": 
                return <div className="ui-customer-payment-methods-option-house-account"><FontAwesomeIcon icon={faHouse}/></div>;
            case "credit_book":
                return <div className="ui-customer-payment-methods-option-credit-book"><FontAwesomeIcon icon={faWallet}/></div>;
            case "gift_card":
                return <div className="ui-customer-payment-methods-option-gift-card"><FontAwesomeIcon icon={faGiftCard}/></div>;
            default:
                return <div className="ui-customer-payment-methods-default"><FontAwesomeIcon icon={faDollar}/></div>;
        }
    }

    function handleOptionClick(option: TFacilityPaymentOption & {open?: boolean}) {
        const updatedPaymentOptions = paymentOptionState?.map(paymentOption => ({...paymentOption, open: paymentOption?.type === "card" ? true : paymentOption?.id === option?.id ? !paymentOption?.open : false}));

        ReactDOM.unstable_batchedUpdates(() => {
            setPaymentOptionState(updatedPaymentOptions);
            setPaymentMethodsState(prev => ({...prev, giftCardPin: "", giftCardCode: "", paymentOption: !option?.open === false ? null : option, creditBook: null, accountId: null, giftCardBalance: null}));
            setGiftCardState(prev => ({...prev, newGiftCard: false, validatedGiftCard: false}));
        });
    }

    async function handleApplyGiftCard() {
        const validated = await validateGiftCard();
        setGiftCardState(prev => ({...prev, validatedGiftCard: validated, newGiftCard: !validated}));
    }

    function handleCardSectionChange(
        e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
      ) {
        setNewCardState(prev => ({...prev, elementComplete: {...prev.elementComplete, [e.elementType]: e.complete}}));
      }

    function closeNewCardModal() {
        elements.getElement(CardNumberElement).clear();
        elements.getElement(CardExpiryElement).clear();
        elements.getElement(CardCvcElement).clear();
        setNewCardState(prev => ({...prev, active: false, save: false}));
    }

    //Add new credit card
    async function handleAddNewCard() {
        if (!stripe || !elements) {
          dispatch(showError(t("Error occurred before card could save. Please reattempt.")));
          return;
        }
        const tempCreditCards = creditCardState.cards ? [...creditCardState.cards] : [];
        dispatch(enqueue());
        try {
          const stripePaymentMethodRes = await stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement("cardNumber"),
          });
    
          if (stripePaymentMethodRes.paymentMethod) {
            const setupRes = await PostSetup({ facility_id: facility?.id }, false);
    
            if (setupRes.status !== StatusCode.OK) {
              dispatch(showError(t("Error setting up card")));
            }
            if (stripePaymentMethodRes.paymentMethod.id !== "undefined" && setupRes.status === StatusCode.OK) {
              const stripeConfirmCardRes = await stripe.confirmCardSetup(
                setupRes.data.setup_intent.client_secret as string,
                {
                  payment_method: stripePaymentMethodRes.paymentMethod.id,
                },
              );
              if (stripeConfirmCardRes.error) {
                dispatch(showError(stripeConfirmCardRes.error.message));
              } else {
                const postPaymentRes = await PostPaymentMethod(
                  {
                    payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
                    save_card: newCardState?.save,
                    facility_id: facility?.id,
                  },
                  false,
                );
                if (postPaymentRes.status !== StatusCode.OK) {
                  dispatch(showError(t("Error adding card")));
                } else {
                  tempCreditCards.push(postPaymentRes.data as ICustomerCreditCard);
                  ReactDOM.unstable_batchedUpdates(() => {
                    setCreditCardState(prev => ({...prev, cards: tempCreditCards}));
                    setPaymentMethodsState(prev => ({...prev, creditCard: postPaymentRes?.data}));
                  });
                  void closeNewCardModal();
                  dispatch(showSuccess(t("Successfully added card")));
                }
              }
            }
          } else {
            dispatch(showError(stripePaymentMethodRes.error.message));
          }
        } catch (e) {
          console.error(e);
          dispatch(dequeue());
        }
        dispatch(dequeue());
      }

    const elementsComplete =
    newCardState.elementComplete.cardNumber === true &&
    newCardState.elementComplete.cardExpiry === true &&
    newCardState.elementComplete.cardCvc === true;

    return (
        <>
        <div className="ui-customer-payment-methods">
            {paymentOptionState?.filter(option => 
            option?.type !== "card" &&
            !(option?.type === "gift_card" && giftCardState?.giftCards?.length < 1)
             && !(option?.type === "account" && houseAccountState?.accounts?.length < 1)
              && !(option?.type === "credit_book" && creditBookState?.creditBooks?.length < 1))?.map((option, index) => {
                return (
                    <div key={index} className="ui-customer-payment-methods-option">
                        <div className="ui-customer-payment-methods-option-title-section" onClick={() => handleOptionClick(option)}>
                        <div className="flex items-center gap-2">
                            {paymentOptionIcon(option)}
                            <p className="ui-customer-payment-methods-option-title">{option?.type === "card" ? t("Credit Card") : t(option?.title)}</p>
                        </div>
                        <FontAwesomeIcon icon={option?.open ? faMinus : faPlus} className="ui-customer-payment-methods-option-icon"/>
                        </div>

                        {/* Gift Cards */}
                        {option?.type === "gift_card" && option?.open ? (
                            <div>
                        <div className="ui-customer-payment-methods-option-container">
                             {giftCardState?.giftCards?.map((giftCard, giftCardIndex) => {
                                 return (
                                     <div 
                                     key={giftCardIndex} 
                                     className={classNames("ui-customer-payment-methods-option-selection", {"ui-customer-payment-methods-option-selection-active": paymentMethodsState?.giftCardCode === giftCard?.code && paymentMethodsState?.giftCardPin === giftCard?.pin})}
                                     onClick={e => {
                                        e.stopPropagation();
                                        ReactDOM.unstable_batchedUpdates(() => {
                                            setPaymentMethodsState(prev => ({...prev, 
                                                giftCardCode: prev?.giftCardCode === giftCard?.code ? null : giftCard?.code, 
                                                giftCardPin: prev?.giftCardPin === giftCard?.pin ? null : giftCard?.pin, 
                                                giftCardBalance: prev?.giftCardCode === giftCard?.code ? null : giftCard?.balance,
                                                paymentOption: option,
                                            }));
                                            setGiftCardState(prev => ({...prev, newGiftCard: false, validatedGiftCard: false}));
                                        });
                                    }}
                                     >
                                         <p className="ui-customer-payment-methods-option-selection-title-primary">{giftCard?.code}</p>
                                         <p className="ui-customer-payment-methods-option-selection-title-secondary">{t("Balance: ")} <LocaleCurrency amount={giftCard?.balance} currency={giftCard?.currency}/></p>
                                     </div>
                                 );
                             })}
                             
                         </div>
                         {!giftCardState?.validatedGiftCard ? (
                             <Button type="text" size="small" onClick={() => {
                                ReactDOM.unstable_batchedUpdates(() => {
                                    setGiftCardState(prev => ({...prev, newGiftCard: !prev?.newGiftCard}));
                                    setPaymentMethodsState(prev => ({...prev, giftCardCode: null, giftCardPin: null, giftCardBalance: null}));
                                });
                             }}><FontAwesomeIcon icon={giftCardState?.newGiftCard ? faMinus : faPlus} className="mr-2"/> {t("Add new gift card")}</Button>
                         ) : null}
                         {giftCardState?.newGiftCard ? (
                            <FormLayout>
                                <FormLayout.Group>
                                <Input label={t("Gift Card Code")} placeholder={t("Enter gift card code")} value={paymentMethodsState?.giftCardCode} onChange={e => setPaymentMethodsState(prev => ({...prev, giftCardCode: e.target.value}))} id="giftCardCode"/>
                                <Input label={t("PIN")} placeholder={"1010"} value={paymentMethodsState?.giftCardPin} onChange={e => setPaymentMethodsState(prev => ({...prev, giftCardPin: e.target.value}))} id="giftCardPin"/>
                                <Button size="small" type="primary" loading={paymentMethodsState?.giftCardBalance === undefined} onClick={handleApplyGiftCard} disabled={!paymentMethodsState?.giftCardCode || !paymentMethodsState?.giftCardPin || termsChecked !== undefined ? !termsChecked : false}>{t("Apply")}</Button>
                                </FormLayout.Group>
                            </FormLayout>
                         ) : null}
                         {!giftCardState?.newGiftCard && giftCardState?.validatedGiftCard ? (
                                <div className="flex items-center gap-1 justify-between mt-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        <Icon icon={"check"} type="success" theme="light-circle"/>
                                        <p className="font-medium"><LocaleCurrency amount={remainingPaymentAmount > 0 ? paymentMethodsState?.giftCardBalance : remainingPaymentAmount + paymentMethodsState?.giftCardBalance} currency={facility?.default_currency_code ?? "cad"}/> {t("gift card credit will be applied to this order")}</p>
                                    </div>
                                    <FontAwesomeIcon icon={faXmark} className="cursor-pointer" onClick={() => {
                                        ReactDOM.unstable_batchedUpdates(() => {
                                            setPaymentMethodsState(prev => ({...prev, giftCardBalance: null, giftCardCode: "", giftCardPin: ""}));
                                            setGiftCardState(prev => ({...prev, validatedGiftCard: false, newGiftCard: false}));
                                        });
                                    }}/>
                                    </div>
                            ) : null}
                         </div>
                        ) : null}

                        {/* Credit Books */}
                        {option?.type === "credit_book" && option?.open ? (
                            <div>
                                 <div className="ui-customer-payment-methods-option-container">
                                    {creditBookState?.creditBooks?.map((creditBook, creditBookIndex) => {
                                        return (
                                            <div 
                                            key={creditBookIndex} 
                                            className={classNames("ui-customer-payment-methods-option-selection", {"ui-customer-payment-methods-option-selection-active": paymentMethodsState?.creditBook?.id == creditBook?.id})}
                                            onClick={e => {
                                               e.stopPropagation();
                                               setPaymentMethodsState(prev => ({...prev, creditBook: creditBook, paymentOption: option}));
                                           }}
                                            >
                                                <p className="ui-customer-payment-methods-option-selection-title-primary">{creditBook?.title}</p>
                                                <p className="ui-customer-payment-methods-option-selection-title-secondary">{t("Balance: ")} <LocaleCurrency amount={creditBook?.balance} currency={creditBook?.currency}/></p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null} 

                        {/* House Accounts */}
                        {option?.type === "account" && option?.open ? (
                            <div>
                                 <div className="ui-customer-payment-methods-option-container">
                                    {houseAccountState?.accounts?.map((houseAccount, houseAccountIndex) => {
                                        return (
                                            <div 
                                            key={houseAccountIndex} 
                                            className={classNames("ui-customer-payment-methods-option-selection", {"ui-customer-payment-methods-option-selection-active": paymentMethodsState?.accountId == houseAccount?.id})}
                                            onClick={e => {
                                               e.stopPropagation();
                                               setPaymentMethodsState(prev => ({...prev, accountId: houseAccount?.id, paymentOption: option, creditCard: !creditCardRequired ? null : prev.creditCard}));
                                           }}
                                            >
                                                <p className="ui-customer-payment-methods-option-selection-title-primary">{houseAccount?.title}</p>
                                                <p className="ui-customer-payment-methods-option-selection-title-secondary">{t("Balance: ")} <LocaleCurrency amount={houseAccount?.balance} currency={houseAccount?.currency}/></p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                       
                        
                    </div>
                );
            })}
        </div>

        <br />
        {remainingPaymentAmount && remainingPaymentAmount > 0 ? (
            <>
            <p className="ui-customer-payment-methods-remaining-amount">{t("Pay remaining amount: ")} <LocaleCurrency amount={remainingPaymentAmount} currency={facility?.default_currency_code ?? "cad"}/></p>
            <br />
            </>
        ) : null}
        <div className="ui-customer-payment-methods">
        {paymentOptionState?.filter(option => option?.type === "card")?.map((option, index) => {
                return (
                    <div key={index} className="ui-customer-payment-methods-option">
                        <div className="ui-customer-payment-methods-option-title-section">
                        <div className="flex items-center gap-2">
                            {paymentOptionIcon(option)}
                            <p className="ui-customer-payment-methods-option-title">{option?.type === "card" ? t("Credit Card") : t(option?.title)}</p>
                        </div>
                        </div>
                        {/* Credit Card */}
                        {option?.type === "card" && option?.open ? (
                            <div>
                                <div className="ui-customer-payment-methods-option-container">
                                    
                                    {creditCardState?.cards?.length === 0 && creditCardRequired ? (
                                        <Callout title={t("Credit Card Required")} content={t("A credit card is required to hold this booking")} type="info"/>
                                    ) : creditCardState?.cards?.map((card, cardIndex) => {
                                        return (
                                        <div 
                                            key={cardIndex} 
                                            className={classNames("ui-customer-payment-methods-option-selection", {"ui-customer-payment-methods-option-selection-active": card?.id === paymentMethodsState?.creditCard?.id})}
                                            onClick={e => {
                                                const resetPaymentMethods = remainingPaymentAmount !== undefined ? remainingPaymentAmount <= 0 : false;
                                                e.stopPropagation();
                                                setPaymentMethodsState(prev => ({...prev, 
                                                    creditCard: prev?.creditCard?.id === card?.id && !creditCardRequired ? null : card,
                                                    paymentOption: (resetPaymentMethods || prev.paymentOption?.type === "account") && !creditCardRequired ? null : prev.paymentOption,
                                                    giftCardBalance: resetPaymentMethods && !creditCardRequired ? null : prev.giftCardBalance,
                                                    giftCardCode: resetPaymentMethods && !creditCardRequired ? "" : prev.giftCardCode,
                                                    giftCardPin: resetPaymentMethods && !creditCardRequired ? "" : prev.giftCardPin,
                                                    creditBook: resetPaymentMethods && !creditCardRequired ? null : prev.creditBook,
                                                    accountId: !creditCardRequired ? null : prev.accountId,
                                                }));
                                                if (resetPaymentMethods) {
                                                    setGiftCardState(prev => ({...prev, validatedGiftCard: false, newGiftCard: false}));
                                                }
                                            }}
                                            >
                                            <div className="flex items-center gap-2">
                                                <PaymentMethodIcon brand={card?.brand} size="md"/>
                                                <div>
                                                    <p className="ui-customer-payment-methods-option-selection-title-primary">{valueToString(card?.brand)} {t("ending in")} {card?.last4}</p>
                                                    <p className="ui-customer-payment-methods-option-selection-title-secondary">{t("Expiry: ")} {card?.exp_month}/{card?.exp_year}</p>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                                <Button type="text" size="small" onClick={() => setNewCardState(prev => ({...prev, active: true}))}><FontAwesomeIcon icon={faPlus} className="mr-2"/> {t("Add new payment method")}</Button>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    {/* Add new credit card panel (mobile) */}
        <Panel
          open={newCardState.active && windowSize.width < MOBILE_WIDTH}
          onClose={() => closeNewCardModal()}
          title={t("Add New Credit Card")}
          animate
          primaryAction={{
            onClick: () => handleAddNewCard(),
            label: t("Add Card"),
            disabled: !elementsComplete,
          }}
          secondaryAction={{
            onClick: () => closeNewCardModal(),
            label: t("Cancel"),
          }}
        >
              <FormLayout>
                <FormLayout.Group>
                  {windowSize.width < MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
                </FormLayout.Group>
                <FormLayout.Group>
                  <Checkbox
                    id="saveCard"
                    size="small"
                    checked={newCardState?.save}
                    onChange={e => setNewCardState(prev => ({...prev, save: e.target.checked}))}
                    label={t("Save card for future bookings")}
                  />
                </FormLayout.Group>
              </FormLayout>
        </Panel>
    {/* Add new credit card modal (desktop) */}
    <Portal >
        <Sheet
          size="small"
          height="flexible"
          open={newCardState.active && windowSize.width > MOBILE_WIDTH}
          closable
          title={t("Add New Credit Card")}
          cancelText={t("Cancel")}
          okText={t("Add Card")}
          onOk={handleAddNewCard}
          okDisabled={!elementsComplete ? true : false}
          onCancel={() => closeNewCardModal()}
          backDropCancel={false}
        >
        <FormLayout>
            <FormLayout.Group>
                {windowSize.width > MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
            </FormLayout.Group>
            <FormLayout.Group>
                <Checkbox
                id="saveCard"
                size="small"
                checked={newCardState?.save}
                onChange={e => setNewCardState(prev => ({...prev, save: e.target.checked}))}
                label={t("Save card for future bookings")}
                />
            </FormLayout.Group>
        </FormLayout>

        </Sheet>
    </Portal>
        </>
    );
}